import React, { useState } from 'react';
import { CustomColumn, QuestionType } from '../../../store/Models/Surveys/SurveyDataModel';
import DateInput from '../DateInput';
import TextInput from '../TextInput';
import { ToxnotReactModal, ToxnotReactModalBody, ToxnotReactModalFooter } from "../ToxnotReactModal";
import { IStringLocalizer } from '../../../utilities/IStringLocalizer';
import { DropdownMenu } from '../DropdownMenu';
import DataTable from '../DataTable';

interface CustomDataTableProps {
    columns: string[];
    rows: string[][];
    editor?: boolean;
    index?: number;
    addButtonText: string;
    onResponseChange: (value: any) => void;
    customColumns: CustomColumn[];
    dragDropEnabled?: boolean;
    localizer: IStringLocalizer;
}

const CustomDataTable: React.FC<CustomDataTableProps> = ({ columns, rows, editor = true, index, addButtonText, onResponseChange, customColumns, dragDropEnabled = false, localizer }) => {
    const modalId = `custom-data-table-modal-${index}`;
    const [rowsValue, setRowsValue] = useState(rows || []);
    const [currentRowIndex, setCurrentRowIndex] = useState(-1);
    const [currentEditModel, setCurrentEditModel] = useState(columns.map(cc => ""));
    const [currentValidationModel, setCurrentValidationModel] = useState(customColumns.map(cc => { return { valid: true } }));
    const [submittedState, setSubmittedState] = useState(false);

    const getFriendlyNameForOptions = (option: string) => {
        return option;
    }

    const getCustomColumnField = (colIndex: number) => {
        const customColumn = customColumns[colIndex];

        if (!customColumn) {
            return null;
        }

        const colValue = currentEditModel[colIndex] ?? "";

        switch (customColumn.customColumnType) {
            case QuestionType.Text:
                return <TextInput value={colValue} disabled={!editor} onValueChange={(value: string) => onFieldEdit(colIndex, value)} />
            case QuestionType.Datetime:
                return <DateInput value={colValue} onValueChange={(value: string) => onFieldEdit(colIndex, value)} />
            case QuestionType.MultipleDropdown:
                const answerOptions = customColumn.predefinedAnswers ?? [];

                return <DropdownMenu options={[" ", ...answerOptions?.map(a => a.nativeAnswer)]}
                    onOptionSelected={(value: string) => onFieldEdit(colIndex, value)}
                    getFriendlyNameForOptions={getFriendlyNameForOptions}
                    value={colValue}
                    getUniqueValueForOption={(o: any) => o}
                />
            default:
                console.log(`Unknown question type: ${"question.type"}`);
                return null;
        }
    }

    const onFieldEdit = (index: number, value: string) => {
        const editModel = [ ...currentEditModel ];
        const validationModel = [ ...currentValidationModel ];

        editModel[index] = value;
        validationModel[index].valid = value.trim().length > 0;

        setCurrentEditModel(editModel);
        setCurrentValidationModel(validationModel);
    }

    const openModal = () => {
        $(`#${modalId}`).modal("show");
    }

    const closeModal = () => {
        $(`#${modalId}`).modal("hide");
    }

    const validationMapper = (currentValue: string, currentIndex: number) => {
        const currentCustomColumn = customColumns[currentIndex];

        if (currentCustomColumn.required) {
            if (currentCustomColumn.customColumnType === QuestionType.MultipleDropdown.toString()) {
                const predefinedAnswers = currentCustomColumn.predefinedAnswers?.map(pa => pa.nativeAnswer);

                if (!predefinedAnswers?.some(pa => pa === currentValue)) {
                    return { valid: false };
                }
            } else if (currentValue.trim().length === 0) {
                return { valid: false };
            }
        }

        return { valid: true };
    }

    const addRow = () => {
        const emptyEditModel = columns.map(cc => "");

        setCurrentRowIndex(rowsValue.length);
        setCurrentEditModel(emptyEditModel);
        setCurrentValidationModel(emptyEditModel.map(validationMapper));

        openModal();
    }

    const editRow = (rowIndex: number) => {
        setCurrentRowIndex(rowIndex);
        setCurrentEditModel(rowsValue[rowIndex]);
        setCurrentValidationModel(rowsValue[rowIndex].map(validationMapper));

        openModal();
    }

    const submit = () => {
        setSubmittedState(true);

        if (currentValidationModel.some(cv => cv.valid === false)) {
            return;
        }

        const updatedMatrix = [...rowsValue];

        if (updatedMatrix.length === 0) {
            updatedMatrix.push(currentEditModel);
        } else {
            updatedMatrix[currentRowIndex] = currentEditModel;
        }

        setRowsValue(updatedMatrix);
        onResponseChange(updatedMatrix);
        setSubmittedState(false);

        closeModal();
    }

    const cancel = () => {
        setCurrentRowIndex(-1);
        setCurrentEditModel(columns.map(cc => ""));
        setCurrentValidationModel(customColumns.map(cc => { return { valid: true } }));
        setSubmittedState(false);
    }

    const onChange = (value: any) => {
        setRowsValue(value);
        onResponseChange(value);
    }

    return (
        <div className="generic-table-parent data-table-parent">
            <DataTable
                localizer={localizer}
                columns={columns}
                rows={rowsValue}
                onResponseChange={onChange}
                radioColumns={[]}
                inlineEditor={false}
                addButtonText={addButtonText}
                customAdd={addRow}
                customEdit={editRow}
                dragDropEnabled={dragDropEnabled}
                requiredColumns={customColumns.map(cc => cc.required === true)}
            />

            <ToxnotReactModal modalId={modalId} localizedTitle={localizer.localizeString("Add/Edit Row")}>
                <ToxnotReactModalBody>
                    {customColumns.map((cc, cIndex) => {
                        return <div className="form-group" key={cIndex}>
                            <label>
                                {cc.customColumnName}
                                {cc.required && <span className="p-relative m-l-5"><span className="text-danger p-absolute required-asterisk">*</span></span>}
                            </label>
                            {getCustomColumnField(cIndex)}
                            {submittedState === true && currentValidationModel[cIndex].valid === false && <p className="m-t-5 text-danger">{cc.validationMessage}</p>}
                        </div>
                    })}
                </ToxnotReactModalBody>
                <ToxnotReactModalFooter onCancel={cancel} localizer={localizer}>
                    <button type="button" className="btn btn-toxnot-primary" onClick={submit}>{localizer.localizeString("Submit")}</button>
                </ToxnotReactModalFooter>
            </ToxnotReactModal>
        </div>
    )
}

export default CustomDataTable;